import * as React from "react";
import { Location, useLocation } from "react-router-dom";
import styled, { css } from "styled-components";
import { Icon } from "./Icon";
import { VisuallyHidden } from "./VisuallyHidden";
import { style } from "./style";
import { focusOutline } from "./utils/focusOutlineStyle";

const DOLogoLink = styled.a<{ $isMobile: boolean }>`
  all: unset;
  border: 0;
  margin: 0;
  color: ${({ $isMobile }) => ($isMobile ? style.colors.blue.dark : style.colors.white)};
  display: block;
  position: relative;
  transition: opacity ${style.vars.transitionSpeed.long} linear;
  ${({ $isMobile }) =>
    !$isMobile &&
    css`
      &:hover {
        color: ${style.colors.white};
      }
    `}
  &:focus-visible, &:focus {
    ${focusOutline()}
  }
`;

const StyledLogo = styled(Icon)`
  height: 32px;
  width: 32px;
  vertical-align: top;
`;

type Props = {
  isMobile?: boolean;
  className?: string;
  logoClickHref?: string;
  teamsV3?: boolean;
};

/**
 * Merges in the context params `i` from the current URL if one isn't passed in.
 */
const mergeHref = (href: string, location: Location): string => {
  if (typeof URL === "undefined" || typeof URLSearchParams === "undefined") {
    return href;
  }

  const [pathnameStr, searchParamsStr] = href.split("?");
  const searchParams = new URLSearchParams(searchParamsStr);

  if (!searchParams.has("i")) {
    const currentUrlParams = new URLSearchParams(location.search);
    const context = currentUrlParams.get("i");

    if (context) {
      searchParams.set("i", context);
    }
  }

  return `${pathnameStr}?${searchParams.toString()}`;
};

export const DOLogoNav: React.FC<Props> = ({ isMobile = false, className, logoClickHref = "/" }) => {
  const location = useLocation();
  const href = React.useMemo(() => mergeHref(logoClickHref, location), [logoClickHref, location]);

  return (
    <DOLogoLink href={href} $isMobile={isMobile} className={className}>
      <StyledLogo icon="logo" />
      <VisuallyHidden>DigitalOcean</VisuallyHidden>
    </DOLogoLink>
  );
};
